import { z } from 'zod';

/**
 * Bidirectional Zod transforms for backend epoch-seconds dates.
 *
 * The backend uses `number` (seconds since epoch); the entity sees `Date`.
 * We define BOTH directions as schemas so the same source of truth handles
 * deserialize (in queryFn) AND serialize (in mutationFn / write helpers).
 *
 * Pattern from https://tkdodo.eu/blog/type-safe-react-query — Zod schema as
 * the single contract per field per direction.
 */

/** API → entity (epoch seconds → Date) */
export const epochSecondsToDate = z.number().transform((s) => new Date(s * 1000));
export const epochSecondsToDateNullable = z
  .number()
  .nullable()
  .transform((s) => (s == null ? null : new Date(s * 1000)));
export const epochSecondsToDateOptional = z
  .number()
  .optional()
  .transform((s) => (s == null ? undefined : new Date(s * 1000)));

/**
 * Drop `undefined` keys from a partial wire payload before sending. Use as
 * the trailing `.transform(stripUndefined)` on the entity → API schema so
 * the API body only carries fields the caller actually set.
 */
export function stripUndefined<T extends Record<string, unknown>>(value: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

/** Entity → API (Date → epoch seconds) */
export const dateToEpochSeconds = z.date().transform((d) => Math.floor(d.getTime() / 1000));
export const dateToEpochSecondsNullable = z
  .date()
  .nullable()
  .transform((d) => (d == null ? null : Math.floor(d.getTime() / 1000)));
export const dateToEpochSecondsOptional = z
  .date()
  .optional()
  .transform((d) => (d == null ? undefined : Math.floor(d.getTime() / 1000)));
